const DEFAULT_OPS_HOST = "ellyanna.com";
const DEFAULT_MARKETPLACE_HOST = "boothshare.ellyanna.com";
const DEFAULT_ACCOUNTING_HOST = "accounting.ellyanna.com";
const LOCAL_HOSTS = new Set(["", "localhost", "127.0.0.1", "0.0.0.0"]);
const MARKETPLACE_HOST_ALIASES = new Set([
  "boothshare.ellyanna.com",
  "www.boothshare.ellyanna.com",
  // Legacy hosts should continue to enter the marketplace experience while DNS
  // redirects are being moved to boothshare.ellyanna.com.
  "myboothshare.com",
  "www.myboothshare.com",
]);
const ACCOUNTING_HOST_ALIASES = new Set([
  "accounting.ellyanna.com",
  "www.accounting.ellyanna.com",
  // Legacy hosts should continue to enter the accounting experience while DNS
  // redirects are being moved to accounting.ellyanna.com.
  "salonbusinesscpa.com",
  "www.salonbusinesscpa.com",
  "salonbusinesscpa.ca",
  "www.salonbusinesscpa.ca",
  "salonbusinessaccounting.com",
  "www.salonbusinessaccounting.com",
  "salonbusinessaccounting.ca",
  "www.salonbusinessaccounting.ca",
]);

const MARKETPLACE_ROUTE_PREFIXES = [
  "/marketplace/",
  "/access-needed",
  "/search",
  "/workspaces/",
  "/checkout/",
  "/account/",
  "/find-salons",
  "/join-salon",
  "/create-booth",
  "/my-booths",
  "/edit-booth/",
  "/host-start",
  "/host/",
  "/booths/",
];

const OPERATIONS_ROUTE_PREFIXES = [
  "/manager/",
  "/technician/",
  "/admin/",
  "/superadmin",
  "/reports",
  "/setup",
  "/my-salons",
  "/select-salon",
];

function normalizeHostname(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/\.$/, "");
}

function normalizePathname(pathname = "/") {
  if (!pathname) return "/";
  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

function matchesRoutePrefix(pathname = "/", prefixes = []) {
  const normalizedPath = normalizePathname(pathname);
  return prefixes.some((prefix) =>
    prefix.endsWith("/")
      ? normalizedPath.startsWith(prefix)
      : normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)
  );
}

function getConfiguredHost(envValue, fallbackValue) {
  return normalizeHostname(envValue || fallbackValue);
}

export const OPS_HOST = getConfiguredHost(
  process.env.REACT_APP_OPS_HOST,
  DEFAULT_OPS_HOST
);

export const MARKETPLACE_HOST = getConfiguredHost(
  process.env.REACT_APP_MARKETPLACE_HOST,
  DEFAULT_MARKETPLACE_HOST
);

export const ACCOUNTING_HOST = getConfiguredHost(
  process.env.REACT_APP_ACCOUNTING_HOST,
  DEFAULT_ACCOUNTING_HOST
);

export function isLocalHostname(hostname = "") {
  return LOCAL_HOSTS.has(normalizeHostname(hostname));
}

export function isAccountingHost(hostname = "") {
  const normalizedHost = normalizeHostname(hostname);
  return (
    normalizedHost === ACCOUNTING_HOST ||
    normalizedHost === `www.${ACCOUNTING_HOST}` ||
    ACCOUNTING_HOST_ALIASES.has(normalizedHost)
  );
}

export function isMarketplaceHostname(hostname = "") {
  const normalizedHost = normalizeHostname(hostname);
  return (
    normalizedHost === MARKETPLACE_HOST ||
    normalizedHost === `www.${MARKETPLACE_HOST}` ||
    MARKETPLACE_HOST_ALIASES.has(normalizedHost)
  );
}

export function isCanonicalHost(hostname = "", targetHost = "") {
  const normalizedHost = normalizeHostname(hostname);
  const normalizedTarget = normalizeHostname(targetHost);
  if (!normalizedHost || !normalizedTarget) {
    return false;
  }
  return (
    normalizedHost === normalizedTarget || normalizedHost === `www.${normalizedTarget}`
  );
}

export function buildHostUrl(targetHost, pathname = "/", search = "", hash = "") {
  const normalizedTarget = normalizeHostname(targetHost);
  const normalizedPath = normalizePathname(pathname);
  const safeSearch = search || "";
  const safeHash = hash || "";
  return `https://${normalizedTarget}${normalizedPath}${safeSearch}${safeHash}`;
}

export function isMarketplaceRoute(pathname = "/") {
  return matchesRoutePrefix(pathname, MARKETPLACE_ROUTE_PREFIXES);
}

export function isOperationsRoute(pathname = "/") {
  return matchesRoutePrefix(pathname, OPERATIONS_ROUTE_PREFIXES);
}

export function getCrossDomainRedirectUrl({
  pathname = "/",
  search = "",
  hash = "",
  hostname = "",
  isMarketplaceHost = false,
  isAccountingHost: isAccountingHostContext = false,
}) {
  const normalizedHost = normalizeHostname(hostname);
  const normalizedPath = normalizePathname(pathname);
  if (isLocalHostname(normalizedHost)) {
    return null;
  }

  if (isAccountingHostContext || isAccountingHost(normalizedHost)) {
    return null;
  }

  if (isMarketplaceHost && normalizedPath === "/superadmin") {
    return isCanonicalHost(normalizedHost, MARKETPLACE_HOST)
      ? null
      : buildHostUrl(MARKETPLACE_HOST, normalizedPath, search, hash);
  }

  if (isMarketplaceHost && normalizedPath === "/register") {
    return buildHostUrl(OPS_HOST, "/register", search, hash);
  }

  if (isMarketplaceHost && isOperationsRoute(pathname)) {
    return buildHostUrl(OPS_HOST, pathname, search, hash);
  }

  if (isMarketplaceHost && !isCanonicalHost(normalizedHost, MARKETPLACE_HOST)) {
    return buildHostUrl(MARKETPLACE_HOST, pathname, search, hash);
  }

  if (!isMarketplaceHost && isMarketplaceRoute(pathname)) {
    return buildHostUrl(MARKETPLACE_HOST, pathname, search, hash);
  }

  return null;
}
